import * as React from 'react';
import mergeRefs from 'react-merge-refs';
import IconCheckmark from '../icons/icon_checkmark';
import IconMinus from '../icons/icon_minus';

import useStyles from './use_styles';
import {
  checkboxStates,
  checkboxStringProp,
  checkedStatesToBool,
} from './utils';
import propTypes from './prop_types';
import { CheckboxInterface, CheckedType } from './types';
import useControlled from '../utils/use_controlled';
import { useTheme } from '@veneer/theme';
import highContrastMediaQuery from '../utils/high_contrast_media_query';

import { deprecatedColors } from '../utils/deprecated_primitives';
import { ColorType } from '../utils/color_prop';

let colorWarned = false;

const Checkbox = React.forwardRef<
  HTMLInputElement,
  CheckboxInterface &
    Omit<React.HTMLAttributes<HTMLInputElement>, keyof CheckboxInterface>
>(
  (
    {
      'aria-disabled': ariaDisabled,
      checked: checkedProp,
      className,
      color,
      customStyle,
      defaultChecked: defaultCheckedProp = false,
      disabled: disabledProp = false,
      icon,
      id,
      label,
      name,
      onChange,
      value,
      ...inputProps
    },
    ref
  ) => {
    if (
      process.env.NODE_ENV === 'development' &&
      !colorWarned &&
      deprecatedColors.includes(color as ColorType)
    ) {
      console.warn(
        `@veneer/core: The color: ${color} has been deprecated since v3.121.0 and it'll be removed by October 30, 2025`
      );
      colorWarned = true;
    }

    const inputRef = React.useRef<HTMLInputElement | null>();
    const handleRef = mergeRefs([ref, inputRef]);

    const defaultCheckedPropToBool = checkedStatesToBool(defaultCheckedProp);

    const [checked, setChecked, isControlled] = useControlled<CheckedType>({
      controlled: checkedProp,
      default: defaultCheckedProp,
    });

    const hasIcon = !!icon;

    const isIndeterminate =
      checkboxStates(checked) === checkboxStringProp.indeterminated;

    const isChecked = checkboxStates(checked) === checkboxStringProp.checked;
    const disabled =
      ariaDisabled === true || ariaDisabled === 'true' || disabledProp;

    const handleChange = React.useCallback<
      React.ChangeEventHandler<HTMLInputElement>
    >(
      (event) => {
        if (disabled || ariaDisabled) return;
        if (!isControlled) setChecked(event.target.checked);
        onChange?.(event);
      },
      [ariaDisabled, disabled, isControlled, onChange, setChecked]
    );

    const style = useStyles();

    const iconNode =
      icon &&
      React.cloneElement(icon, {
        className: 'vn-checkbox__span--custom_icon',
        color,
        customStyle: {
          padding: 0,
        },

        disabled,
        filled: isChecked,
      });

    React.useEffect(() => {
      if (inputRef.current && isIndeterminate) {
        inputRef.current.indeterminate = isIndeterminate;
      }
    }, [isIndeterminate, inputRef]);

    const iconChecked = !icon && (
      <IconCheckmark
        size={24}
        className="vn-checkbox__span--icon"
        customStyle={[style.icon.base, isChecked && style.icon.show]}
      />
    );

    const iconIndeterminate = !icon && (
      <IconMinus
        size={24}
        className="vn-checkbox__span--icon"
        customStyle={[style.icon.base, isIndeterminate && style.icon.show]}
      />
    );

    const theme = useTheme();
    const isHc = theme.mode === 'contrast';

    return (
      <label
        className={className}
        css={[
          style.root.base,
          !hasIcon && (isChecked || isIndeterminate)
            ? style.root.checked
            : style.root.unchecked,
          isHc && [
            style.root.hc.base,
            !hasIcon && (isChecked || isIndeterminate)
              ? style.root.hc.checked
              : style.root.hc.unchecked,
          ],
          disabled && style.root.disabled,
          highContrastMediaQuery(theme),
          customStyle,
        ]}
      >
        <input
          aria-disabled={ariaDisabled}
          checked={isControlled ? checkedStatesToBool(checked) : undefined}
          defaultChecked={isControlled ? undefined : defaultCheckedPropToBool}
          disabled={disabledProp}
          id={id}
          name={name}
          onChange={handleChange}
          ref={handleRef}
          type="checkbox"
          value={value}
          css={style.interactive}
          {...inputProps}
        />

        <span
          aria-hidden
          className="vn-checkbox__span"
          css={[
            style.decorative.base,
            !hasIcon && [
              style.decorative.noIcon.base,
              (isChecked || isIndeterminate) &&
                style.decorative.noIcon.checked.base,
              disabled && [
                style.decorative.noIcon.disabled,
                (isChecked || isIndeterminate) &&
                  style.decorative.noIcon.checked.disabled,
              ],
            ],
          ]}
        >
          {iconNode}
          {iconChecked}
          {iconIndeterminate}
        </span>
        {label && (
          <span className="label vn-checkbox__span--text" css={style.label}>
            {label}
          </span>
        )}
      </label>
    );
  }
);

Checkbox.propTypes = propTypes;

Checkbox.displayName = 'Checkbox';

export default Checkbox;
